'use client';

import React, { useState } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import {
  MapPin,
  Building2,
  Calendar,
  Layers,
  FileText,
  Phone,
  MessageCircle,
  Download,
  CheckCircle2,
  ArrowRight,
  TrendingUp,
  ShieldCheck,
  Maximize2,
  Truck,
  Sparkles,
  DollarSign,
  ChevronRight,
  Share2,
  ExternalLink,
  Clock,
  Compass,
} from 'lucide-react';

interface WarehousePlotTemplateProps {
  page?: any;
  meta?: any;
  slug?: string;
}

export default function WarehousePlotTemplate({
  page,
  meta,
  slug,
}: WarehousePlotTemplateProps) {
  const [formData, setFormData] = useState({
    fullName: '',
    company: '',
    phone: '',
    email: '',
    purpose: 'Request Sales Proposal',
    message: '',
  });

  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [copied, setCopied] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMessage('');

    if (!formData.fullName || !formData.phone || !formData.email) {
      setErrorMessage('Please fill in your name, phone / WhatsApp, and email.');
      return;
    }

    setLoading(true);
    try {
      const res = await fetch('/api/submissions', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          form_type: 'warehouse_plot_inquiry',
          name: formData.fullName,
          email: formData.email,
          phone: formData.phone,
          details: {
            company: formData.company,
            purpose: formData.purpose,
            message: formData.message,
            property: 'Large Warehouse & Storage Plot - Dubai-Al Ain Road (E66)',
            source: 'Warehouse Plot Full-Width Landing Page',
          },
        }),
      });

      if (!res.ok) throw new Error('Submission failed');

      setSubmitted(true);
    } catch (err: any) {
      setErrorMessage('Unable to submit inquiry right now. Please reach us directly via WhatsApp.');
    } finally {
      setLoading(false);
    }
  };

  const handleShare = () => {
    if (typeof window !== 'undefined') {
      navigator.clipboard.writeText(window.location.href);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    }
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC] text-[#0F172A] font-[Montserrat,sans-serif] selection:bg-[#006838] selection:text-white">
      <Header />

      {/* 1. TOP SUB-HEADER BAR */}
      <div className="pt-[80px] bg-[#0c1f17] border-b border-white/10 text-xs text-white">
        <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 py-3 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2 text-white">
            <Link href="/" className="!text-white hover:!text-white/80 transition-colors font-medium">
              Home
            </Link>
            <ChevronRight size={13} className="text-white/60" />
            <span className="text-white font-medium">
              Exclusive Opportunities
            </span>
            <ChevronRight size={13} className="text-white/60" />
            <span className="text-white font-semibold truncate max-w-[260px] sm:max-w-none">
              Dubai-Al Ain Road (E66) Warehouse Plot
            </span>
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={handleShare}
              className="inline-flex items-center gap-1.5 px-3 py-1 rounded-md bg-white/10 hover:bg-white/20 text-white transition-all text-xs"
            >
              <Share2 size={13} />
              <span>{copied ? 'Copied!' : 'Share'}</span>
            </button>
            <a
              href="/uploads/dubai-al-ain-road-warehouse-plot-proposal.pdf"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 px-3.5 py-1 rounded-md bg-[#006838] hover:bg-[#82BC41] hover:text-black text-white font-semibold transition-all text-xs shadow-sm"
            >
              <Download size={13} />
              <span>Download PDF Proposal</span>
            </a>
          </div>
        </div>
      </div>

      {/* 2. STYLISH FULL-WIDTH CINEMATIC HERO BANNER */}
      <section className="relative w-full overflow-hidden bg-slate-950 text-white">
        {/* Full-width Background Image */}
        <div className="absolute inset-0 z-0">
          <Image
            src="/uploads/banner/dubai-warehouse-logistics-hero.jpg"
            alt="Dubai Logistics & Warehousing Plot Along Dubai-Al Ain Road (E66)"
            fill
            priority
            className="object-cover object-center transform scale-105 animate-in fade-in duration-1000"
          />
          {/* Multi-layered cinematic gradient overlays */}
          <div className="absolute inset-0 bg-gradient-to-r from-black/95 via-black/75 to-black/50 z-10"></div>
          <div className="absolute inset-0 bg-gradient-to-t from-[#0c1f17] via-transparent to-black/60 z-10"></div>
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(130,188,65,0.15),transparent_60%)] z-10"></div>
        </div>

        {/* Hero Content Container */}
        <div className="relative z-20 max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 pt-12 pb-20 sm:pt-16 sm:pb-28 lg:pt-20 lg:pb-32">
          <div className="max-w-4xl space-y-6">
            
            {/* Tag / Eyebrow */}
            <div className="flex flex-wrap items-center gap-2.5">
              <span className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full bg-[#006838] text-white text-xs font-black tracking-widest uppercase border border-[#82BC41]/40 shadow-lg backdrop-blur-md">
                <Sparkles size={13} className="text-[#82BC41]" />
                Rare & Exclusive Opportunity
              </span>
              <span className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full bg-black/60 text-amber-300 text-xs font-semibold border border-white/15 backdrop-blur-md">
                <MapPin size={13} />
                Dubai – Al Ain Road (E66), Dubai
              </span>
            </div>

            {/* Sub-headline */}
            <p className="text-amber-400 font-bold tracking-[0.2em] text-xs sm:text-sm uppercase">
              Prime Land • Strong Returns • Lasting Value
            </p>

            {/* Main Title */}
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-black text-white tracking-tight leading-[1.1]">
              Large Warehouse & Storage Plot for Sale
            </h1>

            {/* Narrative / Context from PDF */}
            <p className="text-white/85 text-base sm:text-lg md:text-xl font-normal leading-relaxed max-w-3xl">
              A substantial land opportunity for investors, developers and businesses seeking a long-term base for logistics, warehousing and storage.
            </p>

            {/* Hero Quick Action Buttons */}
            <div className="flex flex-wrap items-center gap-3.5 pt-2">
              <a
                href="#enquiry-form"
                className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl bg-[#006838] hover:bg-[#82BC41] hover:text-black text-white font-extrabold text-sm sm:text-base transition-all duration-200 shadow-xl shadow-green-950/40 hover:shadow-2xl"
              >
                <span>Request Property Details</span>
                <ArrowRight size={18} />
              </a>
              <a
                href="https://wa.me/971504331896?text=Hi%20Plotae,%20I%20am%20interested%20in%20the%201.5M%20Sq.Ft%20Warehouse%20%26%20Storage%20Plot%20along%20Dubai-Al%20Ain%20Road%20(E66)."
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl bg-[#25D366] hover:bg-[#20ba59] text-white font-bold text-sm sm:text-base transition-all duration-200 shadow-xl"
              >
                <MessageCircle size={18} />
                <span>Enquire on WhatsApp</span>
              </a>
              <a
                href="#property-overview"
                className="inline-flex items-center justify-center gap-2 px-5 py-3.5 rounded-xl bg-white/10 hover:bg-white/20 text-white font-semibold text-sm backdrop-blur-md border border-white/20 transition-all duration-200"
              >
                <span>View Full Overview</span>
              </a>
            </div>

          </div>
        </div>

        {/* 3. FULL-WIDTH FLOATING METRICS STRIP */}
        <div className="relative z-20 border-t border-white/15 bg-black/60 backdrop-blur-xl">
          <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 py-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 lg:gap-8 divide-y md:divide-y-0 md:divide-x divide-white/10">
              
              {/* Metric 1 */}
              <div className="pt-3 md:pt-0 md:px-4 first:px-0">
                <div className="flex items-center gap-2 text-white/60 text-xs font-semibold tracking-wider uppercase mb-1">
                  <Maximize2 size={14} className="text-[#82BC41]" />
                  <span>Land Area</span>
                </div>
                <div className="text-2xl sm:text-3xl lg:text-4xl font-black text-white">
                  1.5 Million
                </div>
                <div className="text-xs text-[#82BC41] font-semibold mt-0.5">
                  Sq. Ft. Total Plot Area
                </div>
              </div>

              {/* Metric 2 */}
              <div className="pt-3 md:pt-0 md:px-4">
                <div className="flex items-center gap-2 text-white/60 text-xs font-semibold tracking-wider uppercase mb-1">
                  <DollarSign size={14} className="text-amber-400" />
                  <span>Selling / Takeover</span>
                </div>
                <div className="text-2xl sm:text-3xl lg:text-4xl font-black text-white">
                  AED 20 Million
                </div>
                <div className="text-xs text-amber-300 font-semibold mt-0.5">
                  Competitive Takeover Price
                </div>
              </div>

              {/* Metric 3 */}
              <div className="pt-3 md:pt-0 md:px-4">
                <div className="flex items-center gap-2 text-white/60 text-xs font-semibold tracking-wider uppercase mb-1">
                  <Clock size={14} className="text-[#82BC41]" />
                  <span>Lease Contract</span>
                </div>
                <div className="text-2xl sm:text-3xl lg:text-4xl font-black text-white">
                  30 Years
                </div>
                <div className="text-xs text-white/70 font-semibold mt-0.5">
                  Long-Term Horizon
                </div>
              </div>

              {/* Metric 4 */}
              <div className="pt-3 md:pt-0 md:px-4">
                <div className="flex items-center gap-2 text-white/60 text-xs font-semibold tracking-wider uppercase mb-1">
                  <TrendingUp size={14} className="text-[#82BC41]" />
                  <span>Base Master Rent</span>
                </div>
                <div className="text-2xl sm:text-3xl lg:text-4xl font-black text-white">
                  AED 2 / sq. ft.
                </div>
                <div className="text-xs text-[#82BC41] font-semibold mt-0.5">
                  Annual Fixed Base Rent
                </div>
              </div>

            </div>
          </div>
        </div>
      </section>

      {/* 4. MAIN DOSSIER & ENQUIRY SECTION */}
      <section id="property-overview" className="py-16 lg:py-24 max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-start">
          
          {/* LEFT 7 COLUMNS: Complete Property Overview, Possibilities & Advantages */}
          <div className="lg:col-span-7 space-y-12">
            
            {/* PROPERTY OVERVIEW TABLE CARD */}
            <div className="bg-white rounded-2xl p-6 sm:p-8 shadow-sm border border-slate-200">
              <div className="flex items-center justify-between pb-6 border-b border-slate-100">
                <div>
                  <span className="text-[#006838] font-bold text-xs uppercase tracking-widest">
                    Key Specifications
                  </span>
                  <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-900 mt-1">
                    Property Overview
                  </h2>
                </div>
                <div className="w-12 h-12 rounded-xl bg-[#006838]/10 flex items-center justify-center text-[#006838]">
                  <FileText size={24} />
                </div>
              </div>

              {/* Data Table */}
              <div className="divide-y divide-slate-100 mt-4 text-sm sm:text-base">
                <div className="py-4 flex flex-col sm:flex-row sm:items-center justify-between gap-1">
                  <span className="font-semibold text-slate-500 text-sm">Location</span>
                  <span className="font-bold text-slate-900 flex items-center gap-1.5">
                    <MapPin size={16} className="text-[#006838]" />
                    Directly along Dubai-Al Ain Road (E66), Dubai
                  </span>
                </div>

                <div className="py-4 flex flex-col sm:flex-row sm:items-center justify-between gap-1">
                  <span className="font-semibold text-slate-500 text-sm">Land Area</span>
                  <span className="font-bold text-slate-900">
                    Approximately 1.5 million sq. ft.
                  </span>
                </div>

                <div className="py-4 flex flex-col sm:flex-row sm:items-center justify-between gap-1">
                  <span className="font-semibold text-slate-500 text-sm">Selling / Takeover Price</span>
                  <span className="font-black text-[#006838] text-lg sm:text-xl">
                    AED 20 million
                  </span>
                </div>

                <div className="py-4 flex flex-col sm:flex-row sm:items-center justify-between gap-1">
                  <span className="font-semibold text-slate-500 text-sm">Lease Term</span>
                  <span className="font-bold text-slate-900">
                    30 years
                  </span>
                </div>

                <div className="py-4 flex flex-col sm:flex-row sm:items-center justify-between gap-1">
                  <span className="font-semibold text-slate-500 text-sm">Base Annual Master Rent</span>
                  <span className="font-bold text-slate-900">
                    AED 2 per sq. ft.
                  </span>
                </div>

                <div className="py-4 flex flex-col sm:flex-row sm:items-center justify-between gap-1">
                  <span className="font-semibold text-slate-500 text-sm">Proposed Uses</span>
                  <span className="font-bold text-slate-900">
                    Warehousing, logistics and storage
                  </span>
                </div>

                <div className="py-4 flex flex-col sm:flex-row sm:items-center justify-between gap-1">
                  <span className="font-semibold text-slate-500 text-sm">Subdivision & Subleasing</span>
                  <span className="inline-flex items-center gap-1.5 font-bold text-emerald-700 bg-emerald-50 px-3 py-1 rounded-md text-xs sm:text-sm">
                    <CheckCircle2 size={15} />
                    Possible, subject to approvals & lease terms
                  </span>
                </div>
              </div>

              {/* Commercial Footnote */}
              <div className="mt-6 p-4 rounded-xl bg-slate-50 border border-slate-200 text-xs text-slate-600 leading-relaxed">
                <span className="font-bold text-slate-800">Commercial Disclosure: </span>
                The sale relates to the long-term leasehold opportunity. Annual master rent, development expenses, and applicable transaction costs are additional.
              </div>
            </div>

            {/* ONE LARGE SITE. MULTIPLE POSSIBILITIES */}
            <div className="bg-white rounded-2xl p-6 sm:p-8 shadow-sm border border-slate-200">
              <span className="text-[#006838] font-bold text-xs uppercase tracking-widest">
                Masterplan Potential
              </span>
              <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-900 mt-1">
                One Large Site. Multiple Possibilities.
              </h2>
              <p className="text-slate-600 mt-2 leading-relaxed">
                Develop a dedicated warehouse and storage destination, establish facilities for your own operations, or create serviced plots for commercial tenants.
              </p>

              <div className="mt-6 space-y-3.5">
                <p className="text-xs uppercase tracking-wider font-bold text-slate-400">
                  Subject to the required approvals, the site offers potential for:
                </p>

                {[
                  {
                    title: 'Warehouse Development for Leasing or Operational Use',
                    desc: 'Build high-specification multi-bay ambient or temperature-controlled logistics units for corporate fleets or long-term institutional tenants.',
                    icon: <Warehouse size={18} />,
                  },
                  {
                    title: 'Open Storage & Logistics Facilities',
                    desc: 'Utilize expansive yard space for heavy fleet parking, machinery yards, bulk material laydown, container storage, and freight routing.',
                    icon: <Truck size={18} />,
                  },
                  {
                    title: 'Subdivision into Smaller Commercial Plots',
                    desc: 'Divide the 1.5M sq. ft. parcel into independent serviced plots tailored for SMEs, contractors, and private logistics operators.',
                    icon: <Layers size={18} />,
                  },
                  {
                    title: 'Subleasing to Multiple Occupiers',
                    desc: 'Generate diversified, recurring annual cash flow by structuring individual sub-leases across a diversified roster of commercial users.',
                    icon: <Building2 size={18} />,
                  },
                  {
                    title: 'Phased Development Based on Business Requirements',
                    desc: 'Stagger capital expenditure dynamically over time with staged zoning execution as operational demand scales.',
                    icon: <TrendingUp size={18} />,
                  },
                ].map((item, idx) => (
                  <div
                    key={idx}
                    className="p-4 rounded-xl bg-slate-50 hover:bg-[#006838]/5 border border-slate-200/80 hover:border-[#006838]/30 transition-all flex items-start gap-3.5"
                  >
                    <div className="w-10 h-10 rounded-lg bg-[#006838] text-white flex-shrink-0 flex items-center justify-center mt-0.5 shadow-sm">
                      {item.icon}
                    </div>
                    <div>
                      <h4 className="font-bold text-slate-900 text-sm sm:text-base">
                        {item.title}
                      </h4>
                      <p className="text-xs sm:text-sm text-slate-600 mt-1 leading-relaxed">
                        {item.desc}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* WHY THIS PROPERTY STANDS OUT */}
            <div className="bg-white rounded-2xl p-6 sm:p-8 shadow-sm border border-slate-200">
              <span className="text-[#006838] font-bold text-xs uppercase tracking-widest">
                Strategic Advantages
              </span>
              <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-900 mt-1 mb-6">
                Why This Property Stands Out
              </h2>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {[
                  {
                    title: 'Direct Dubai-Al Ain Road Location',
                    desc: 'Positioned directly along the E66 corridor, offering a prominent location for a future logistics and warehousing hub with seamless highway access.',
                    badge: 'Corridor E66',
                  },
                  {
                    title: 'Substantial Land Area',
                    desc: 'Approximately 1.5 million sq. ft. provides massive scope for multiple facilities, wide internal roads, loading docks, and supporting infrastructure.',
                    badge: '1.5M Sq. Ft.',
                  },
                  {
                    title: '30-Year Lease Horizon',
                    desc: 'A long-term lease provides the essential stability and legal foundation to plan institutional development and establish income-producing operations.',
                    badge: '30-Year Horizon',
                  },
                  {
                    title: 'Low Base Master Rent',
                    desc: 'A base master rent of AED 2 per sq. ft. annually creates tremendous commercial upside and supports competitive tenant sub-leasing models.',
                    badge: 'AED 2 / sq.ft.',
                  },
                  {
                    title: 'Flexible Development Potential',
                    desc: 'The possibility of subdivision and subleasing allows buyers to consider a versatile range of tenant sizes, usages, and phased masterplans.',
                    badge: 'Multi-Tenant',
                    fullWidth: true,
                  },
                ].map((pillar, idx) => (
                  <div
                    key={idx}
                    className={`p-5 rounded-xl border border-slate-200 bg-gradient-to-br from-white to-slate-50/70 hover:shadow-md transition-all ${
                      pillar.fullWidth ? 'sm:col-span-2' : ''
                    }`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-bold uppercase tracking-wider text-[#006838] bg-[#006838]/10 px-2.5 py-0.5 rounded-full">
                        {pillar.badge}
                      </span>
                      <CheckCircle2 size={16} className="text-[#82BC41]" />
                    </div>
                    <h3 className="font-extrabold text-slate-900 text-base mb-1.5">
                      {pillar.title}
                    </h3>
                    <p className="text-xs sm:text-sm text-slate-600 leading-relaxed">
                      {pillar.desc}
                    </p>
                  </div>
                ))}
              </div>
            </div>

            {/* LOCATION HIGHLIGHT BANNER */}
            <div className="relative rounded-2xl overflow-hidden bg-slate-900 p-8 text-white">
              <div className="relative z-10 space-y-3">
                <span className="text-xs font-bold uppercase tracking-widest text-[#82BC41]">
                  Connectivity & Logistics Network
                </span>
                <h3 className="text-xl sm:text-2xl font-black text-white">
                  Prime Position Along Dubai-Al Ain Road (E66)
                </h3>
                <p className="text-white/80 text-sm leading-relaxed max-w-2xl">
                  Strategically positioned for rapid transit to Emirates Road (E611), Sheikh Mohammed Bin Zayed Road (E311), and major freight hubs including Al Maktoum International Airport (DWC) and Jebel Ali Port.
                </p>
                <div className="pt-2 flex flex-wrap gap-4 text-xs text-white/90 font-medium">
                  <span className="flex items-center gap-1.5">
                    <CheckCircle2 size={14} className="text-[#82BC41]" /> Direct highway ingress / egress
                  </span>
                  <span className="flex items-center gap-1.5">
                    <CheckCircle2 size={14} className="text-[#82BC41]" /> Heavy vehicle & freight compliant
                  </span>
                  <span className="flex items-center gap-1.5">
                    <CheckCircle2 size={14} className="text-[#82BC41]" /> Unmatched regional reach
                  </span>
                </div>
              </div>
            </div>

          </div>

          {/* RIGHT 5 COLUMNS: Sticky Enquiry Form & Commercial Action Center */}
          <div className="lg:col-span-5 space-y-6 lg:sticky lg:top-[105px]">
            
            {/* COMMERCIAL SUMMARY CARD */}
            <div className="bg-gradient-to-br from-[#0c241a] to-[#006838] rounded-2xl p-6 text-white shadow-xl border border-white/10">
              <div className="flex items-center justify-between mb-3">
                <span className="text-xs font-bold uppercase tracking-widest text-[#82BC41]">
                  Investment Profile
                </span>
                <span className="text-xs bg-white/20 px-2.5 py-1 rounded text-white font-semibold">
                  30-Year Leasehold
                </span>
              </div>
              <div className="text-3xl sm:text-4xl font-black text-white">
                AED 20,000,000
              </div>
              <div className="text-xs text-white/80 mt-1">
                Selling / Takeover Price for 1.5M Sq. Ft. Parcel
              </div>

              <div className="grid grid-cols-2 gap-3 mt-5 pt-5 border-t border-white/15 text-xs">
                <div>
                  <span className="text-white/60 block">Annual Master Rent:</span>
                  <span className="font-bold text-white text-sm">AED 2 / sq. ft.</span>
                </div>
                <div>
                  <span className="text-white/60 block">Strategic Corridor:</span>
                  <span className="font-bold text-white text-sm">Dubai-Al Ain (E66)</span>
                </div>
              </div>

              <div className="mt-5 flex gap-2">
                <a
                  href="tel:+971504331896"
                  className="flex-1 inline-flex items-center justify-center gap-2 py-3 rounded-xl bg-white/10 hover:bg-white/20 text-white font-bold text-xs transition-all border border-white/20"
                >
                  <Phone size={14} />
                  <span>Call: +971 50 433 1896</span>
                </a>
                <a
                  href="https://wa.me/971504331896?text=Hi%20Plotae,%20I%20am%20interested%20in%20arranging%20a%20site%20visit%20for%20the%20Dubai-Al%20Ain%20Road%20Warehouse%20Plot."
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-1.5 px-4 py-3 rounded-xl bg-[#25D366] hover:bg-[#20ba59] text-white font-bold text-xs transition-all shadow-md"
                >
                  <MessageCircle size={15} />
                  <span>WhatsApp</span>
                </a>
              </div>
            </div>

            {/* ENQUIRY FORM */}
            <div id="enquiry-form" className="bg-white rounded-2xl p-6 sm:p-8 shadow-lg border border-slate-200">
              <div className="mb-6">
                <span className="text-xs font-bold uppercase tracking-widest text-[#006838]">
                  Explore the Property
                </span>
                <h3 className="text-xl sm:text-2xl font-black text-slate-900 mt-0.5">
                  Request Property Details
                </h3>
                <p className="text-xs sm:text-sm text-slate-500 mt-1">
                  Request the detailed property proposal, review the commercial terms, and arrange a private site visit.
                </p>
              </div>

              {submitted ? (
                <div className="p-6 rounded-xl bg-emerald-50 border border-emerald-200 text-center space-y-4">
                  <div className="w-14 h-14 bg-emerald-100 text-emerald-600 rounded-full flex items-center justify-center mx-auto">
                    <CheckCircle2 size={32} />
                  </div>
                  <div>
                    <h4 className="text-lg font-bold text-emerald-950">
                      Enquiry Received
                    </h4>
                    <p className="text-xs sm:text-sm text-emerald-800 mt-1">
                      Our commercial land specialists will review your requirements and provide the complete sales proposal package.
                    </p>
                  </div>
                  <div className="pt-2">
                    <a
                      href="https://wa.me/971504331896?text=Hi%20Plotae,%20I%20just%20submitted%20an%20enquiry%20for%20the%20Dubai-Al%20Ain%20Road%20Warehouse%20Plot."
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-[#25D366] text-white font-bold text-xs hover:bg-[#20ba59] transition-all shadow-sm"
                    >
                      <MessageCircle size={15} />
                      <span>Speed up via WhatsApp</span>
                    </a>
                  </div>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4">
                  {errorMessage && (
                    <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-xs font-medium">
                      {errorMessage}
                    </div>
                  )}

                  <div>
                    <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1.5">
                      Full Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      required
                      placeholder="e.g. Tariq Al Mansoor"
                      value={formData.fullName}
                      onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
                      className="w-full px-3.5 py-2.5 text-sm rounded-xl border border-slate-300 focus:outline-none focus:border-[#006838] focus:ring-2 focus:ring-[#006838]/20 transition-all"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1.5">
                      Company Name
                    </label>
                    <input
                      type="text"
                      placeholder="e.g. Al Mansoor Logistics Group"
                      value={formData.company}
                      onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                      className="w-full px-3.5 py-2.5 text-sm rounded-xl border border-slate-300 focus:outline-none focus:border-[#006838] focus:ring-2 focus:ring-[#006838]/20 transition-all"
                    />
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1.5">
                        Phone / WhatsApp <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="tel"
                        required
                        placeholder="+971 50 XXX XXXX"
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        className="w-full px-3.5 py-2.5 text-sm rounded-xl border border-slate-300 focus:outline-none focus:border-[#006838] focus:ring-2 focus:ring-[#006838]/20 transition-all"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1.5">
                        Email Address <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="email"
                        required
                        placeholder="tariq@example.com"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        className="w-full px-3.5 py-2.5 text-sm rounded-xl border border-slate-300 focus:outline-none focus:border-[#006838] focus:ring-2 focus:ring-[#006838]/20 transition-all"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1.5">
                      Next Step / Purpose
                    </label>
                    <select
                      value={formData.purpose}
                      onChange={(e) => setFormData({ ...formData, purpose: e.target.value })}
                      className="w-full px-3.5 py-2.5 text-sm rounded-xl border border-slate-300 focus:outline-none focus:border-[#006838] focus:ring-2 focus:ring-[#006838]/20 transition-all bg-white"
                    >
                      <option value="Request Sales Proposal">Request the Sales Proposal</option>
                      <option value="Arrange a Site Visit">Arrange a Site Visit</option>
                      <option value="Discuss Sublease & Subdivision">Discuss Sublease & Subdivision Potential</option>
                      <option value="Commercial Terms Consultation">Commercial Terms Consultation</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1.5">
                      Message / Special Requirements
                    </label>
                    <textarea
                      rows={3}
                      placeholder="Tell us about your operational timeline or intended development use..."
                      value={formData.message}
                      onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                      className="w-full px-3.5 py-2.5 text-sm rounded-xl border border-slate-300 focus:outline-none focus:border-[#006838] focus:ring-2 focus:ring-[#006838]/20 transition-all resize-none"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full py-3.5 px-4 rounded-xl bg-[#006838] hover:bg-[#00522c] text-white font-extrabold text-sm tracking-wide transition-all duration-200 shadow-md flex items-center justify-center gap-2 disabled:opacity-60 cursor-pointer"
                  >
                    {loading ? (
                      <span className="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                    ) : (
                      <>
                        <span>Submit Property Enquiry</span>
                        <ArrowRight size={16} />
                      </>
                    )}
                  </button>

                  <div className="pt-2 text-[11px] text-slate-500 leading-tight">
                    By submitting this form, you agree to receive property details and commercial updates from Plotae. Your data is protected under our Privacy Policy.
                  </div>
                </form>
              )}

              {/* PDF Proposal Download Prompt */}
              <div className="mt-6 pt-5 border-t border-slate-100 flex items-center justify-between text-xs">
                <div className="flex items-center gap-2 text-slate-600">
                  <FileText size={16} className="text-[#006838]" />
                  <span>Download full proposal document</span>
                </div>
                <a
                  href="/uploads/dubai-al-ain-road-warehouse-plot-proposal.pdf"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-bold text-[#006838] hover:underline inline-flex items-center gap-1"
                >
                  <Download size={13} />
                  <span>Download PDF</span>
                </a>
              </div>
            </div>

            {/* DIRECT BROKER HOTLINE */}
            <div className="bg-slate-50 rounded-2xl p-5 border border-slate-200/90 flex items-center justify-between">
              <div>
                <span className="text-[11px] font-bold uppercase tracking-wider text-slate-500 block">
                  Direct Line & WhatsApp
                </span>
                <span className="text-slate-900 font-extrabold text-base">
                  +971 50 433 1896
                </span>
              </div>
              <a
                href="https://wa.me/971504331896?text=Hi%20Plotae,%20I%20am%20inquiring%20about%20the%20Dubai-Al%20Ain%20Road%20Warehouse%20Plot."
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-xl bg-[#25D366] text-white flex items-center justify-center hover:scale-105 transition-all shadow-md"
              >
                <MessageCircle size={20} />
              </a>
            </div>

          </div>
        </div>

        {/* 5. LEGAL DISCLAIMER FOOTNOTE (From PDF) */}
        <div className="mt-16 pt-8 border-t border-slate-200 text-xs text-slate-400 text-center max-w-4xl mx-auto leading-relaxed">
          <p>
            Permitted uses, development, subdivision, and subleasing are subject to authority approvals and final lease terms. Project visuals and renderings are illustrative.
          </p>
          <p className="mt-1">
            Plotae is Dubai&apos;s verified land and development plot marketplace. Official property transaction facilitation subject to statutory DLD regulations.
          </p>
        </div>
      </section>

      {/* FOOTER */}
      <Footer />
    </div>
  );
}

function Warehouse({ size = 20, className = '' }: { size?: number; className?: string }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
    >
      <path d="M22 8.35V20a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V8.35A2 2 0 0 1 3.26 6.5l8-4a2 2 0 0 1 1.48 0l8 4A2 2 0 0 1 22 8.35Z" />
      <path d="M6 18h12" />
      <path d="M6 14h12" />
      <rect width="12" height="12" x="6" y="10" />
    </svg>
  );
}
